/**
 * @module app.core
 */

import { autoinject, transient } from "aurelia-framework";
import { ApiModelIdentifier } from 'app/shared/models/essette-api';
import { StandardLoadOptions } from 'app/shared/lib/essette-site';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { IContactLogConfig } from "app/core/models/essette-interfaces";
import * as api from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as UM from 'app/um/models/essette-api';

const defaultConfig: IContactLogConfig = {
    serviceRootUrl: ""  // root of "Core" queries. Should not use this one because it lacks proper context for some of its actions
}

@transient()
@autoinject
export class ContactLogService {

    ////****************** Fields and Properties ************************************////
    private isConfigured: boolean = false;
    private config: IContactLogConfig;


    ////****************** Functions ************************************************////
    constructor(protected api: api.Api) {
        this.config = defaultConfig;
    }

    public configure(config: IContactLogConfig): void {
        this.config = Object.assign({}, defaultConfig, config); // Clones the incoming config object parameter, using defaultConfig as fallback for any missing properties, and assigns it to this.config
        this.isConfigured = true;
    }
    private checkConfiguration(): void {
        if(!this.isConfigured) {
            throw Error("Contact Log service requires module-specific configuration! Use configure() method.");
        }
    }


    ////****************** Queries **************************************************////
    public async loadContactLogByTarget(targetId: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<Core.ContactLog>> {
        this.checkConfiguration();
        const url = `${this.config.serviceRootUrl}/${targetId}/contactlog`;

        const query = new api.ApiQuery(url)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadContactLogByMember(memberId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<Core.ContactLog>> {
        this.checkConfiguration();
        const url = `${this.config.rollupConfig?.serviceRootUrl}/${memberId}/rollup/contactlog`;

        const query = new api.ApiQuery(url)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    public async loadContactLogMethods(options?: StandardLoadOptions, includes: string[] = [], withOutcomes: boolean = false, fields: string[] = []): Promise<api.ApiQueryResponse<CM.CaseContactLogMethod | UM.AuthContactLogMethod>> {
        this.checkConfiguration();
        const url = `${this.config.serviceRootUrl}/contactlog/methods`;

        const query = new api.ApiQuery(url)
            .applyStandardLoadOptions(options);

        if(withOutcomes) {
            query.includeWithState(options, 'outcomes')
                .filterExists('outcomes');

            if(!includes.includes('outcomes')) {
                includes.push('outcomes');
            }
        }

        query.include(...includes);
        query.field(...fields);

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    public async saveContactLog(contactLog: CM.CaseContactLog & UM.AuthContactLog): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action('insert', `${this.config.serviceRootUrl}/contactlog`, contactLog);

        return await this.api.post(action);
    }

    public async saveContactLogMethod(contactLogMethod: CM.CaseContactLogMethod & UM.AuthContactLogMethod): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionName = !contactLogMethod.id ? 'insert' : 'update';
        const url = `${this.config.serviceRootUrl}/contactlog/methods/${contactLogMethod.id ? `${contactLogMethod.id}` : ``}`;
        const action = new api.ApiAction(actionName, url, contactLogMethod);

        return await this.api.post(action);
    }

    public async saveContactLogOutcome(contactLogOutcome: CM.CaseContactLogOutcome & UM.AuthContactLogOutcome): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionName = !contactLogOutcome.id ? 'insert' : 'update';
        const url = `${this.config.serviceRootUrl}/contactlog/outcomes/${contactLogOutcome.id ? `${contactLogOutcome.id}` : ``}`;
        const action = new api.ApiAction(actionName, url, contactLogOutcome);

        return await this.api.post(action);
    }

    public async activateContactLogMethod(contactLogMethodId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `${this.config.serviceRootUrl}/contactlog/methods/${contactLogMethodId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateContactLogOutcome(contactLogOutcomeId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `${this.config.serviceRootUrl}/contactlog/outcomes/${contactLogOutcomeId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }
}
